import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Trophy, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MatchCenter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [aiSummary, setAiSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');

  useEffect(() => {
    fetchMatches();
    if (id) {
      fetchMatchDetails(id);
    }
  }, [id]);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches?limit=50`);
      setMatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch matches');
      console.error('Error:', error);
    }
  };

  const fetchMatchDetails = async (matchId) => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}`);
      setSelectedMatch(response.data);
      fetchAISummary(matchId);
    } catch (error) {
      toast.error('Failed to fetch match details');
    }
  };

  const fetchAISummary = async (matchId) => {
    setLoadingSummary(true);
    try {
      const response = await axios.get(`${API}/matches/${matchId}/summary`, {
        timeout: 10000  // 10 second timeout
      });
      setAiSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to fetch AI summary:', error);
      setAiSummary('AI summary temporarily unavailable. Match details shown above.');
    } finally {
      setLoadingSummary(false);
    }
  };

  const filteredMatches = filterStatus === 'All' 
    ? matches 
    : matches.filter(m => m.status === filterStatus);

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Header */}
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="page-title">Match Center</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!selectedMatch ? (
          <>
            {/* Filters */}
            <div className="flex gap-4 mb-8" data-testid="filter-tabs">
              {['All', 'Live', 'Upcoming', 'Completed'].map((status) => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  onClick={() => setFilterStatus(status)}
                  data-testid={`filter-${status.toLowerCase()}`}
                >
                  {status}
                </Button>
              ))}
            </div>

            {/* Matches Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMatches.map((match) => (
                <Card 
                  key={match.id} 
                  className={`glass-card cursor-pointer hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
                  onClick={() => navigate(`/matches/${match.id}`)}
                  data-testid={`match-card-${match.id}`}
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className={`badge badge-${match.status.toLowerCase()}`}>{match.status}</span>
                      <span className="text-xs text-gray-400">{match.format}</span>
                    </div>
                    
                    <h3 className="font-bold text-lg">{match.title}</h3>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">{match.team1}</span>
                        <span className="font-mono font-bold text-green-400">{match.score_team1 || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">{match.team2}</span>
                        <span className="font-mono font-bold text-green-400">{match.score_team2 || '-'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-400">
                      <MapPin className="w-4 h-4 mr-1" />
                      {match.venue}
                    </div>
                    
                    {match.winner && (
                      <div className="flex items-center text-sm text-green-400">
                        <Trophy className="w-4 h-4 mr-1" />
                        Winner: {match.winner}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Match Details with Tabs */}
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className={theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6">
            <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="match-details">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className={`badge badge-${selectedMatch.status.toLowerCase()}`}>
                    {selectedMatch.status}
                  </span>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{selectedMatch.format} • {selectedMatch.competition}</span>
                </div>
                
                <h2 className="text-3xl font-bold">{selectedMatch.title}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="p-6 rounded-xl bg-white/5">
                      <div className="text-gray-400 text-sm mb-2">{selectedMatch.team1}</div>
                      <div className="text-3xl font-bold text-green-400" data-testid="team1-score">
                        {selectedMatch.score_team1 || 'Yet to bat'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-6 rounded-xl bg-white/5">
                      <div className="text-gray-400 text-sm mb-2">{selectedMatch.team2}</div>
                      <div className="text-3xl font-bold text-green-400" data-testid="team2-score">
                        {selectedMatch.score_team2 || 'Yet to bat'}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6 text-sm text-gray-400">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {selectedMatch.venue}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(selectedMatch.date).toLocaleDateString()}
                  </div>
                </div>
                
                {selectedMatch.winner && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center text-green-400">
                      <Trophy className="w-5 h-5 mr-2" />
                      <span className="font-bold">Winner: {selectedMatch.winner}</span>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* AI Summary */}
            <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="ai-summary-section">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-xl font-bold">AI Match Summary</h3>
                </div>
                
                {loadingSummary ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="spinner"></div>
                  </div>
                ) : (
                  <p className="text-gray-300 leading-relaxed" data-testid="ai-summary-text">
                    {aiSummary || 'Generating summary...'}
                  </p>
                )}
              </div>
            </Card>
              </TabsContent>

              <TabsContent value="scorecard" className="mt-6">
                <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="scorecard-section">
                  <div className="space-y-6">
                    <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Scorecard</h3>
                    
                    {selectedMatch.scorecard ? (
                      <>
                        {/* Team 1 Scorecard */}
                        <div className="space-y-4">
                          <h4 className={`text-lg font-semibold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                            {selectedMatch.team1} - {selectedMatch.score_team1 || 'Yet to bat'}
                          </h4>
                          
                          {selectedMatch.scorecard.team1_innings && (
                            <>
                              <div className={`overflow-x-auto rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                                <table className="w-full">
                                  <thead>
                                    <tr className={`${theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'}`}>
                                      <th className={`px-4 py-3 text-left text-sm font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Batsman</th>
                                      <th className={`px-4 py-3 text-center text-sm font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>R</th>
                                      <th className={`px-4 py-3 text-center text-sm font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>B</th>
                                      <th className={`px-4 py-3 text-center text-sm font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>4s</th>
                                      <th className={`px-4 py-3 text-center text-sm font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>6s</th>
                                      <th className={`px-4 py-3 text-center text-sm font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>SR</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedMatch.scorecard.team1_innings.batsmen?.map((batsman, idx) => (
                                      <tr key={`${batsman.name || 'batsman'}-${idx}`} className={`border-b ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                                        <td className={`px-4 py-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                          {batsman.cricclubs_url ? (
                                            <a 
                                              href={batsman.cricclubs_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-blue-500 hover:text-blue-400 hover:underline"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {batsman.name}
                                            </a>
                                          ) : (
                                            batsman.name
                                          )}
                                          {!batsman.out && <span className="text-green-500">*</span>}
                                        </td>
                                        <td className={`px-4 py-3 text-center font-mono font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>{batsman.runs}</td>
                                        <td className={`px-4 py-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{batsman.balls}</td>
                                        <td className={`px-4 py-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{batsman.fours}</td>
                                        <td className={`px-4 py-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{batsman.sixes}</td>
                                        <td className={`px-4 py-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{batsman.strike_rate}</td>
                                      </tr>
                                    ))}
                                    <tr className={`border-t-2 ${theme === 'dark' ? 'border-white/20' : 'border-gray-300'}`}>
                                      <td className={`px-4 py-3 font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Total</td>
                                      <td className={`px-4 py-3 text-center font-mono font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                        {selectedMatch.scorecard.team1_innings.total}/{selectedMatch.scorecard.team1_innings.wickets}
                                      </td>
                                      <td className={`px-4 py-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} colSpan="4">
                                        ({selectedMatch.scorecard.team1_innings.overs} overs, RR: {selectedMatch.scorecard.team1_innings.run_rate})
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              
                              {selectedMatch.scorecard.team1_innings.bowlers && (
                                <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                                  <h5 className={`font-semibold mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Bowling</h5>
                                  <div className="grid grid-cols-5 gap-3 text-sm">
                                    {selectedMatch.scorecard.team1_innings.bowlers.map((bowler, idx) => (
                                      <div key={`${bowler.name}-${idx}`} className="text-center">
                                        <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{bowler.name}</div>
                                        <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{bowler.overs}-{bowler.maidens}-{bowler.runs}-{bowler.wickets}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        <p>Detailed scorecard not available for this match</p>
                        <p className="text-sm mt-2">Scorecard will be added when match data is imported</p>
                      </div>
                    )}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}